import React, { useContext, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';

import classnames from 'classnames';

import NavLink from './NavLink';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AuthContext, appActions, userGroups } from '../context/AuthContext';
import { FaChevronLeft, FaQuestionCircle, FaSearch, FaBookOpen, FaAngleDown } from 'react-icons/fa';

import { Menu, MenuList, MenuButton, MenuItem, MenuLink } from '@reach/menu-button';
import SearchBar from './titleManagement/SearchBar';
import { ReactComponent as GatewayLogo } from '../svg/gateway-logo.svg';
import { useLocalStorage } from 'react-use';
import { usePermissions } from '../hooks/usePermissions';
import InnaAvatar from './chat/InnaAvatar';

const Layout = ({ requiresGroup = false, children, useContainer = true }) => {
    const { state: authState, dispatch } = useContext(AuthContext);
    const [collapseSidebar, setCollapseSidebar] = useState(false);

    const params = useParams();
    const currentContentId = params?.contentId || null;

    const [recentSearches, , removeRecentSearches] = useLocalStorage('previousSearches');

    // Permissions checks
    const hasGroupPerms = usePermissions(requiresGroup);
    const isAdmin = usePermissions(userGroups.ADMIN);

    // Path checks
    const location = useLocation();
    const isTitleMgmtPath = useMemo(() => location.pathname.includes('/title-management'), [location.pathname]);

    const handleLogOut = () => {
        try {
            Auth.signOut();
            dispatch({ type: appActions.LOGOUT });
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="site-row">
            <header className="header bg-white between-md middle-md">
                <div className="pl-2 py-xs-2 py-md-0">
                    <Link className="d-block" to="/">
                        <GatewayLogo className="d-block" width={200} />
                    </Link>
                </div>
                <div className="col-xs-12 col-md-6">
                    <SearchBar />
                </div>
                <div className="user-menu mr-2 ml-2 my-4 my-md-0 ml-md-0">
                    <Menu>
                        <MenuButton className="btn btn-sm btn-secondary">
                            {authState.user &&
                                `${authState.user.attributes.given_name} ${authState.user.attributes.family_name}`}
                            {!authState.user && 'Menu'}
                            <FaAngleDown className="fa-icon d-inline-block ml-1" />
                        </MenuButton>
                        <MenuList>
                            {authState.user && !authState.isLoading && (
                                <MenuLink as={Link} to="/account">
                                    My Account
                                </MenuLink>
                            )}
                            {authState.user && !authState.isLoading && isAdmin && (
                                <MenuLink as={Link} to="/admin/users">
                                    Manage Users
                                </MenuLink>
                            )}
                            {!authState.user && !authState.isLoading && (
                                <MenuLink as={Link} to="/sign-in">
                                    Sign In
                                </MenuLink>
                            )}
                            {authState.user && !authState.isLoading && (
                                <MenuItem onSelect={handleLogOut}>Sign Out</MenuItem>
                            )}
                        </MenuList>
                    </Menu>
                </div>
            </header>
            <nav
                className={classnames('sidebar', {
                    'sidebar-slim': collapseSidebar
                })}
            >
                <div className="sidebar-wrapper txt-secondary">
                    {isTitleMgmtPath && (
                        <ul className="nav">
                            <li
                                className={classnames('nav-item primary', {
                                    active: isTitleMgmtPath
                                })}
                            >
                                <NavLink
                                    className={classnames('nav-link my-3', {
                                        active: isTitleMgmtPath
                                    })}
                                    activeClass="active"
                                    activeWhenExact
                                    to={`/title-management/${currentContentId}`}
                                >
                                    <FaBookOpen className="d-inline-block" />
                                    <span>Title Management</span>
                                </NavLink>
                                <hr className="mt-3 mb-0" />
                                {currentContentId && (
                                    <ul className="nav my-3">
                                        <li className="nav-item">
                                            <NavLink
                                                className="nav-link"
                                                activeClass="active"
                                                activeWhenExact
                                                to={`/title-management/${currentContentId}`}
                                            >
                                                Metadata
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                className="nav-link"
                                                activeClass="active"
                                                activeWhenExact
                                                to={`/title-management/${currentContentId}/product-summary`}
                                            >
                                                Product Summary
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                className="nav-link product-history"
                                                activeClass="active"
                                                activeWhenExact
                                                to={`/title-management/${currentContentId}/product-history`}
                                            >
                                                Product History
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                className="nav-link"
                                                activeClass="active"
                                                activeWhenExact
                                                to={`/title-management/${currentContentId}/push-distribution`}
                                            >
                                                Push Distribution
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                className="nav-link"
                                                activeClass="active"
                                                activeWhenExact
                                                to={`/title-management/${currentContentId}/audio`}
                                            >
                                                Audio
                                            </NavLink>
                                            <div className="audio-anchor-links ml-2 pl-1">
                                                <a
                                                    href={'#audio-sample-section'}
                                                    className="hide"
                                                    id="audio-sample-anchor"
                                                >
                                                    <small>Retail Sample</small>
                                                </a>
                                                <a
                                                    href={'#audio-compare-section'}
                                                    className="hide"
                                                    id="audio-compare-anchor"
                                                >
                                                    <small>Comparing Playlists</small>
                                                </a>
                                                <a
                                                    href={'#audio-aeplaylist-section'}
                                                    className="hide"
                                                    id="audio-aeplaylist-anchor"
                                                >
                                                    <small>AudioEngine Playlist</small>
                                                </a>
                                                <a
                                                    href={'#audio-asfilelist-section'}
                                                    className="hide"
                                                    id="audio-asfilelist-anchor"
                                                >
                                                    <small>AudioService FileList</small>
                                                </a>
                                                <a
                                                    href={'#audio-assamplelist-section'}
                                                    className="hide"
                                                    id="audio-assamplelist-anchor"
                                                >
                                                    <small>AudioService SampleList</small>
                                                </a>
                                                <a
                                                    href={'#audio-asplaylist-section'}
                                                    className="hide"
                                                    id="audio-asplaylist-anchor"
                                                >
                                                    <small>AudioService PlayList</small>
                                                </a>
                                            </div>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        </ul>
                    )}
                    {!isTitleMgmtPath && !!recentSearches?.length && (
                        <ul className="nav">
                            <li className="nav-item txt-white py-5 active">
                                <FaSearch className="d-inline-block mb-1" color="#999" />
                                <span className="txt-bold">Recent Searches</span>
                                <hr className="mt-5" />
                                <ul className="nav">
                                    {recentSearches.map((recentSearch, idx) => (
                                        <li className="nav-item" key={`recentSearch-${idx}`}>
                                            <Link
                                                to={`/search?q=${encodeURIComponent(recentSearch)}`}
                                                className="txt-white nav-link"
                                            >
                                                {recentSearch}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                <button className="btn btn-link txt-white" onClick={() => removeRecentSearches()}>
                                    Clear Recent Searches
                                </button>
                            </li>
                        </ul>
                    )}

                    <ul className="nav">
                        <li className="nav-item">
                            <NavLink className="nav-link nav-link-inna" activeClass="active" to="/chat">
                                <InnaAvatar size={22} /> <span>INNA</span>
                            </NavLink>
                        </li>
                        <li className="nav-item">
                            <a
                                className="nav-link"
                                href="http://support.gateway.audioengine.io/support/home"
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                <FaQuestionCircle className="d-inline-block" /> <span>Visit Help Desk</span>
                            </a>
                        </li>
                    </ul>
                    <button className="btn collapse-button" onClick={() => setCollapseSidebar(!collapseSidebar)}>
                        <FaChevronLeft />
                    </button>
                </div>
            </nav>
            <div className="main-wrapper">
                <main
                    className={classnames('mx-auto', {
                        'container pt-md-6': useContainer
                    })}
                >
                    {!hasGroupPerms && (
                        <div className="txt-center">
                            <h1>NOT AUTHORIZED</h1>
                            <p>It appears you don't have access to this page.</p>
                        </div>
                    )}
                    {hasGroupPerms && children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
